import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from "react";
import { User, Session, AuthChangeEvent } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { clearAllOfflineData } from "@/lib/offline";
import { setSentryUser, clearSentryUser, Sentry } from "@/lib/sentry";

type UserRole = "admin" | "installer" | "partner" | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: UserRole;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);

  // Keskitetty roolin haku - vältetään duplikaatio
  const fetchUserRole = useCallback(async (userId: string): Promise<UserRole> => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          if (import.meta.env.DEV) {
            console.warn("Käyttäjälle ei löytynyt roolia:", userId);
          }
          return null;
        }
        throw error;
      }

      return (data?.role as UserRole) ?? null;
    } catch (error) {
      console.error("Roolin haku epäonnistui:", error);
      if (error instanceof Error && !error.message.includes("JWT")) {
        toast.error("Virhe", {
          description: "Käyttäjäroolin haku epäonnistui. Yritä kirjautua uudelleen.",
        });
      }
      return null;
    }
  }, []);

  const validateGoogleUser = useCallback(async (session: Session): Promise<boolean> => {
    const MAX_RETRIES = 1;
    
    const attemptValidation = async (): Promise<Response> => {
      return fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-google-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );
    };

    try {
      let response: Response | undefined;
      let lastError: Error | null = null;
      
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          response = await attemptValidation();
          break;
        } catch (networkError) {
          lastError = networkError instanceof Error ? networkError : new Error(String(networkError));
          if (attempt < MAX_RETRIES) {
            console.warn(`Validation attempt ${attempt + 1} failed, retrying...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      if (!response) {
        console.error("All validation attempts failed:", lastError);
        Sentry.captureException(lastError || new Error("All validation attempts failed"));
        toast.error("Verkkovirhe", {
          description: "Yhteys palvelimeen epäonnistui. Tarkista verkkoyhteys ja yritä uudelleen.",
        });
        return false;
      }

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          toast.error("Pääsy estetty", {
            description: data?.error || "Sinulla ei ole oikeutta kirjautua järjestelmään.",
          });
          return false;
        }
        
        console.error("Validation server error:", response.status, data);
        toast.error("Palvelinvirhe", {
          description: "Kirjautumisen tarkistus epäonnistui. Yritä hetken kuluttua uudelleen.",
        });
        return false;
      }

      if (!data?.valid) {
        toast.error("Pääsy estetty", {
          description: data?.error || "Sinulla ei ole oikeutta kirjautua järjestelmään.",
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error("Unexpected error validating Google user:", error);
      Sentry.captureException(error);
      toast.error("Virhe", {
        description: "Kirjautumisen tarkistus epäonnistui. Yritä uudelleen.",
      });
      return false;
    }
  }, []);

  const isOAuthCallback = useCallback((): boolean => {
    const hash = window.location.hash;
    const search = window.location.search;
    return hash.includes("access_token") || 
           hash.includes("error") || 
           search.includes("code=") ||
           search.includes("error=");
  }, []);

  const updateAuthState = useCallback(async (
    newSession: Session | null, 
    event?: AuthChangeEvent,
    isInitialLoad: boolean = false
  ) => {
    if (newSession?.user) {
      const userRole = await fetchUserRole(newSession.user.id);
      
      const shouldValidate = !userRole && (
        event === "SIGNED_IN" || 
        (isInitialLoad && isOAuthCallback())
      );
      
      if (shouldValidate) {
        console.log("Validating user without role...");
        const isValid = await validateGoogleUser(newSession);
        
        if (!isValid) {
          console.log("User validation failed, signing out...");
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            console.error("signOut failed during validation:", signOutError);
          } finally {
            window.location.href = "/auth";
          }
          return;
        }
        
        if (!userRole) {
          console.log("User valid but no role assigned yet");
          toast.success("Odota roolin määritystä", {
            description: "Tilisi on hyväksytty. Odota, että ylläpitäjä määrittää roolisi.",
          });
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            console.error("signOut failed during role check:", signOutError);
          } finally {
            window.location.href = "/auth";
          }
          return;
        }
      }
      
      setSession(newSession);
      setUser(newSession.user);
      setRole(userRole);
      if (userRole) {
        setSentryUser(newSession.user.id, userRole);
      }
    } else {
      setSession(null);
      setUser(null);
      setRole(null);
      clearSentryUser();
    }
  }, [fetchUserRole, validateGoogleUser, isOAuthCallback]);

  const updateAuthStateRef = useRef(updateAuthState);
  updateAuthStateRef.current = updateAuthState;

  useEffect(() => {
    let mounted = true;
    let initCompleted = false;

    // Safety timeout - 5 sekuntia
    const safetyTimeout = setTimeout(() => {
      if (mounted && !initCompleted) {
        console.error("Auth initialization timed out after 5s");
        setLoading(false);
      }
    }, 5000);

    let fallbackTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const completeInit = () => {
      if (mounted && !initCompleted) {
        initCompleted = true;
        setLoading(false);
        clearTimeout(safetyTimeout);
        clearTimeout(fallbackTimeoutId);
      }
    };

    // 1. Listener ENSIN - käsittelee myös INITIAL_SESSION
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;

        console.log("Auth event:", event, "session:", !!newSession);

        if (event === "TOKEN_REFRESHED") {
          setSession(newSession);
          return;
        }

        if (event === "INITIAL_SESSION") {
          if (!newSession) {
            // Ei istuntoa — valmis heti
            completeInit();
            return;
          }
          // Istunto olemassa — hae rooli lukon ulkopuolella
          const sessionSnapshot = newSession;
          setTimeout(async () => {
            try {
              await updateAuthStateRef.current(sessionSnapshot, undefined, true);
            } catch (error) {
              console.error("INITIAL_SESSION käsittely epäonnistui:", error);
              Sentry.captureException(error);
            } finally {
              completeInit();
            }
          }, 0);
          return;
        }

        if (event === "SIGNED_IN") {
          setLoading(true);
          const sessionSnapshot = newSession;
          setTimeout(async () => {
            try {
              await updateAuthStateRef.current(sessionSnapshot, event);
            } finally {
              setLoading(false);
            }
          }, 0);
          return;
        }

        if (event === "SIGNED_OUT") {
          setSession(null);
          setUser(null);
          setRole(null);
          clearSentryUser();
          return;
        }

        await updateAuthStateRef.current(newSession, event);
      }
    );

    // 2. Fallback: jos INITIAL_SESSION ei tullut 3s:ssa
    fallbackTimeoutId = setTimeout(async () => {
      if (mounted && !initCompleted) {
        console.warn("INITIAL_SESSION ei saapunut, käytetään getSession fallbackia");
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (mounted && !initCompleted) {
            await updateAuthStateRef.current(session, undefined, true);
          }
        } catch (error) {
          console.error("Fallback getSession epäonnistui:", error);
        } finally {
          completeInit();
        }
      }
    }, 3000);

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      clearTimeout(fallbackTimeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      await clearAllOfflineData();

      setUser(null);
      setSession(null);
      setRole(null);
      clearSentryUser();

      toast.success("Uloskirjautuminen onnistui", {
        description: "Sinut on kirjattu ulos järjestelmästä.",
      });

      window.location.href = "/auth";
    } catch (error) {
      console.error("Uloskirjautuminen epäonnistui:", error);
      toast.error("Virhe", {
        description: "Uloskirjautuminen epäonnistui. Yritä uudelleen.",
      });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
